import {
  ChevronDownIcon,
  ArrowUp01,
  ArrowDown01,
  AlertCircleIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioItem,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { useMemo } from "react";
import { useDSSortStore } from "@/store/ds-listing-sort";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { sortConfigs } from "@/hooks/use-sorted-listings";
import { useWallet } from "@txnlab/use-wallet";

interface ListingSortDropdownProps {
  showWithFarms: boolean;
  showWithoutFarms: boolean;
  setShowWithFarms?: (b: boolean) => void;
  setShowWithoutFarms?: (b: boolean) => void;
}

export function ListingSortDropdown({
  showWithFarms,
  showWithoutFarms,
  setShowWithFarms,
  setShowWithoutFarms,
}: ListingSortDropdownProps) {
  const { activeAddress } = useWallet();
  const { ownFirst, sortValue, setOwnFirst, setSortValue } = useDSSortStore();
  const selectedSort = useMemo(
    () => sortConfigs.find(({ value: v }) => v === sortValue) ?? sortConfigs[0],
    [sortValue]
  );
  const changeSort = (value: string) => {
    const config = sortConfigs.find((s) => s.value === value);
    if (config) {
      setSortValue(config!.value);
    }
  };

  return (
    <span className="relative">
      {!showWithFarms || !showWithoutFarms ? (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger>
              <AlertCircleIcon className="absolute text-primary top-[50%] -translate-y-[100%] -left-3" />
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Some dualSTAKE tokens are hidden due to your preferences.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            {selectedSort.isAscending ? <ArrowUp01 /> : <ArrowDown01 />}
            <span>{selectedSort.display}</span>
            <ChevronDownIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Showing:</DropdownMenuLabel>
          <DropdownMenuCheckboxItem
            checked={ownFirst}
            onCheckedChange={setOwnFirst}
            disabled={!activeAddress}
          >
            My dualSTAKEs first
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          <DropdownMenuRadioGroup
            value={selectedSort.value}
            onValueChange={changeSort}
          >
            <DropdownMenuLabel>Ordering:</DropdownMenuLabel>

            {sortConfigs.map((s) => (
              <DropdownMenuRadioItem key={`sv-${s.value}`} value={s.value}>
                {s.display}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          {setShowWithFarms && setShowWithoutFarms ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={showWithFarms}
                onCheckedChange={setShowWithFarms}
              >
                With farms
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={showWithoutFarms}
                onCheckedChange={setShowWithoutFarms}
              >
                Without farms
              </DropdownMenuCheckboxItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
